import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from "@angular/core";

import {
  CalendarView,
  DayCalendarView,
  FiveMinCalendarView,
  WeekCalendarView,
} from "./calendar-view";
import moment from "moment";
import { CalendarSearchService } from "./services/calendar-search.service";
import { CalendarItem } from "./calendar-item";
import { Router } from "@angular/router";
import {
  BroadcastPresetShow,
  LayoutShow,
  LiveShow,
  MediaShow,
  Show,
} from "../../domain/show";
import { UserService } from "../../domain/user-service";
import { MediaRepository } from "../../domain/media-repository";
import { fromEvent, Subject, Subscription } from "rxjs";
import { auditTime, takeUntil, throttleTime } from "rxjs/operators";
import { UI } from "../../services/ui.service";
import { NgxSmartModalService } from "ngx-smart-modal";
import { MediaInfo, MediaItem, MediaSegment } from "../../domain/media";
import { LiveFeedInfo } from "../../domain/live-feed";
import { Socket } from "../../infrastructure/socket.service";
import { ShowsCache } from "./showsCache";
import { ECopyShowsResult, ShowRepository } from "../../domain/show-repository";
import { CalendarService } from "../../domain/calendar-service";
import { DynamicPromoInfo } from "../../domain/dyanmicPromo";
import { ContextMenuComponent } from "@perfectmemory/ngx-contextmenu";
import { Channel } from "../../domain/channel";
import { FuturePreviewViewModel } from "../channel/future-preview/future-preview.view-model";
import { Layout } from "../../domain/layouts/layout";
import { ELayoutZoneType } from "../../domain/layouts/layoutZone";
import { Block } from "../../domain/blocks/block";
import { CopyShowsViewModel } from "../../services/show/copy-shows.view-model";
import { BroadcastPreset } from "../../domain/broadcasts/broadcast-preset";
import { Switcher } from "../../domain/broadcasts/switcher";
import { BroadcastPresetAudioChannel } from "../../domain/broadcasts/broadcast-preset-audio-channel";
import {
  EChannelBroadcastMode,
  IChannelBroadcastModeChecker,
} from "../../domain/broadcasts/i-channel-broadcast-mode-checker";

const rowMinHeight = 6;

const maxHeightOfMultipleItem = 15;

@Component({
  selector: "icn-calendar-grid",
  templateUrl: "./calendar-grid.component.html",
  styleUrls: ["./calendar-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarGridComponent implements OnChanges, OnDestroy, OnInit {
  readonly rowHeight = 30;

  _headers: Array<string>;
  _dayParts: Array<{ id: number; text: string }> = [];
  _dragMarkTop: number = null;
  _dragMarkLeft: number;
  _dragMarkWidth: number;
  _dragSuggestedTime: moment.Moment;
  calendarColumns: CalendarItem[][] = [];
  showsCache: ShowsCache = new ShowsCache([]);
  _ignoreScroll = false;
  _isLoading = false;
  lineOffsetTop;
  interval;
  mediaUpdatedSubscription: Subscription;
  mediaPackshotUploadFinishedSubscription: Subscription;
  showsChangedSubscription: Subscription;
  changeCurrentDateUpdated: Subscription;
  searchResultHighlightedUpdated: Subscription;
  channelFramerate: number;
  channelTimezone: string;
  videoSubtitlesClicked: CalendarItem;
  currentChannel: Channel;

  feedDefaultDuration = moment.duration(60, "minutes");
  broadcastPresetShowDefaultDuration = moment.duration(60, "minutes");

  @Input() currentView: CalendarView;
  @Input() refreshOnNow: boolean;
  @Input()
  futurePreviewViewModel: FuturePreviewViewModel;

  @Output() refreshOnNowChange = new EventEmitter<any>();
  @Output() currentDateChange = new EventEmitter();
  @Output() zoomIn = new EventEmitter<moment.Moment>();
  @Output() zoomOut = new EventEmitter<moment.Moment>();

  @ViewChild("scrollableContainer", { static: true })
  private _scrollableContainer: ElementRef;
  @ViewChild(ContextMenuComponent, { static: true }) public basicMenu;
  draggedShow: Show;
  durationAdjustShow: Show;

  onMouseWheelEventStreamUnsibscribe$: Subject<void>;
  onMouseWheelEventStream;
  onDragOverHandlerStream: Subject<Show>;
  scheduleUpdateListenerHandler: any;

  constructor(
    private ui: UI,
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private router: Router,
    private mediaRepository: MediaRepository,
    private showRepository: ShowRepository,
    public ngxSmartModalService: NgxSmartModalService,
    private socketService: Socket,
    private calendarService: CalendarService,
    private calendarSearchService: CalendarSearchService,
    private _channelBroadcastModeChecker: IChannelBroadcastModeChecker,
  ) {}

  onSubtitlesClose(subtitlesPopover) {
    subtitlesPopover.hide();
  }

  onClickVideoSubtitles(values, subtitlesPopover) {
    this.videoSubtitlesClicked = values.videoClicked;
    subtitlesPopover.toggle(values.e);
  }

  private alertIfChangingCurrentlyPlayingItemMiddleware = (
    startTime: moment.Moment,
    next,
    media: MediaInfo = null,
  ) => {
    return new Promise((resolve, reject) => {
      if (media) {
        if (
          (media.licenseFrom &&
            startTime.isBefore(media.licenseFrom.startOf("day"))) ||
          (media.licenseTo && startTime.isAfter(media.licenseTo.endOf("day")))
        ) {
          this.ui.globalNotify(this.ui.EVENTS.LicensingIssue, null, null);
          this._isLoading = false;
          this.changeDetectorRef.markForCheck();
          return resolve(false);
        }
      }

      if (startTime.isSameOrBefore(moment())) {
        this.ui.globalNotify(
          this.ui.EVENTS.ChangePlayingItem,
          () => {
            next().then(resolve).catch(reject);
          },
          null,
        );
      } else {
        next().then(resolve).catch(reject);
      }
    });
  };

  get headers(): Array<string> {
    return this._headers;
  }

  get dayParts(): Array<{ id: number; text: string }> {
    return this._dayParts;
  }

  get zoomOutMode(): boolean {
    return this.currentView instanceof WeekCalendarView;
  }

  ngOnInit() {
    this.userService.getCurrentChannel().then((channel) => {
      this.currentChannel = channel;
      this.scheduleUpdateListenerHandler =
        this._onSchedulerUpdatedEventHanlderGrid.bind(this);
      const schedulerUpdatesSubscription = async () =>
        this.socketService.startListeningForSchedulerUpdates(
          channel.id,
          this.scheduleUpdateListenerHandler,
        );
      this.socketService.onHubReconnected.subscribe(async () => {
        this.socketService.stopListeningForSchedulerUpdates(
          this.scheduleUpdateListenerHandler,
        );
        await schedulerUpdatesSubscription();
        this.updateCalendarItems();
      });
      return schedulerUpdatesSubscription();
    });
    this.interval = setInterval(() => {
      this.lineOffsetTop = null;
      this.changeDetectorRef.markForCheck();
    }, 1000);

    this.mediaUpdatedSubscription = this.mediaRepository.mediaUpdated.subscribe(
      () => {
        this.updateCalendarItems();
      },
    );

    this.mediaPackshotUploadFinishedSubscription =
      this.socketService.packshotUploadFinishedChange.subscribe(() => {
        this.updateCalendarItems();
      });

    this.showsChangedSubscription = this.showRepository.onShowsChange.subscribe(
      () => {
        this.updateCalendarItems();
      },
    );

    this.searchResultHighlightedUpdated =
      this.calendarSearchService.highlightUpdate.subscribe(
        this.onsearchResultHighlightedUpdated.bind(this),
      );

    this.changeCurrentDateUpdated =
      this.calendarService.changeCurrentDate.subscribe((date) => {
        this.currentDateChange.emit({
          currentDate: moment.tz(date, this.channelTimezone),
        });
      });

    this.userService.getCurrentChannel().then((channel) => {
      this.channelFramerate = channel.framerate;
      this.channelTimezone = this.userService.getTimezoneValueById(
        channel.timezoneId,
      );
    });

    this.onMouseWheelEventStreamUnsibscribe$ = new Subject<void>();
    this.onMouseWheelEventStream = fromEvent<WheelEvent>(
      this._scrollableContainer.nativeElement,
      "mousewheel",
    );
    this.onMouseWheelEventStream
      .pipe(
        throttleTime(700),
        takeUntil(this.onMouseWheelEventStreamUnsibscribe$),
      )
      .subscribe(this.onMouseWhellScroll.bind(this));

    this.onDragOverHandlerStream = new Subject<Show>();
    this.onDragOverHandlerStream
      .pipe(auditTime(1500))
      .subscribe((selectedShow) => {
        const currentDateBefore = this.currentView.currentDate.clone();
        const newState = this.currentView.onDragOverHandler(null, selectedShow);

        if (newState.currentDate.valueOf() !== currentDateBefore.valueOf()) {
          // console.log('onDragOverHandlerStream -> movinga time');
          this.currentDateChange.emit(newState);
        }
      });
  }

  private _onSchedulerUpdatedEventHanlderGrid(
    channelId: number,
    startTimeUtc: Date,
    endTimeUtc: Date,
  ) {
    console.log(
      "onSchedulerUpdatedEventHanlderGrid",
      channelId,
      startTimeUtc,
      endTimeUtc,
    );
    if (channelId === this.currentChannel.id) {
      this.updateCalendarItems().then();
    }
  }

  updateHeaders() {
    if (this.currentView.daysCount === 1) {
      this._headers = [""];
      return;
    }
    this._headers = this.currentView.formatDates();
  }

  updateDayParts() {
    this._dayParts = this.currentView.createDayParts();
  }

  findShowUnderMouse(time) {
    const showUnderMouse = this.showsCache.getShowDuring(time);
    if (!showUnderMouse) {
      return null;
    }

    if (!!this.draggedShow && this.draggedShow.id === showUnderMouse.id) {
      return null;
    }
    return showUnderMouse;
  }

  onDynamicScriptClick(item: Show, script) {
    const tz = this.channelTimezone;
    this.userService.getCurrentChannel().then((channel) => {
      window.open(
        script.url +
          `?startTime=${item.startTime.clone().tz(tz).format("YYYY-MM-DD[T]HH:mm:ss.SSS[Z]")}&channelId=${channel.id}`,
      );
    });
  }

  getUserDynamicScripts() {
    return this.userService
      .getCurrentUser()
      .dynamicScripts.filter((x) => x.showScriptOnRightClickWithContent);
  }

  getNearestRow(hoverTime: moment.Moment) {
    return moment(
      Math.floor(
        hoverTime.valueOf() /
          (this.currentView.dayPartDuration.asMilliseconds() / 4),
      ) *
        (this.currentView.dayPartDuration.asMilliseconds() / 4),
    );
  }

  suggestTime(hoverTime: moment.Moment) {
    // if mouse is on a show, suggest the closest between start and end of show.
    const showUnderMouse = this.findShowUnderMouse(hoverTime);
    if (!!showUnderMouse) {
      return showUnderMouse.endTime.diff(hoverTime) <
        hoverTime.diff(showUnderMouse.startTime)
        ? showUnderMouse.endTime
        : showUnderMouse.startTime;
    }

    // mouse is not on a show, find nearest row to mouse
    const nearestRow = this.getNearestRow(hoverTime);
    let itemBefore = this.showsCache.getShowEndsBeforeOrAt(hoverTime);
    let itemAfter = this.showsCache.getShowStartsAfterOrAt(hoverTime);

    if (!!this.draggedShow) {
      if (itemBefore && this.draggedShow.id === itemBefore.id) {
        itemBefore = null;
      }
      if (itemAfter && this.draggedShow.id === itemAfter.id) {
        itemAfter = null;
      }
    }

    // if nearest row is on a show after mouse, return start of show
    if (!!itemAfter && nearestRow.valueOf() > itemAfter.startTime.valueOf()) {
      return itemAfter.startTime;
    }
    // if nearest row is on a show before mouse, return end of that show
    if (!!itemBefore && nearestRow.valueOf() < itemBefore.endTime.valueOf()) {
      return itemBefore.endTime;
    }

    // nearest row is on an empty place, return the closest to mouse between
    // (nearest row, start of a show after if exists, end of show before if exists)
    let winner = nearestRow;
    if (
      !!itemAfter &&
      Math.abs(winner.diff(hoverTime)) >
        Math.abs(itemAfter.startTime.diff(hoverTime))
    ) {
      winner = itemAfter.startTime;
    }
    if (
      !!itemBefore &&
      Math.abs(winner.diff(hoverTime)) >
        Math.abs(itemBefore.endTime.diff(hoverTime))
    ) {
      winner = itemBefore.endTime;
    }
    return winner;
  }

  suggestTimeForNewDuration(hoverTime) {
    if (hoverTime <= this.durationAdjustShow.startTime) {
      return this.durationAdjustShow.endTime;
    }

    const nearestRow = this.getNearestRow(hoverTime);
    return nearestRow.diff(this.durationAdjustShow.startTime) > 0
      ? nearestRow
      : this.durationAdjustShow.endTime;
  }

  onVideoDrag(calendarItem: CalendarItem) {
    this.draggedShow = calendarItem.items[0];
    this.changeDetectorRef.markForCheck();
  }

  onVideoDragEnd() {
    this.draggedShow = null;
    this.changeDetectorRef.markForCheck();
  }

  getColumnAndOffsetY(target: any, offsetY: number) {
    if (!target) {
      return null;
    }
    if (target.classList.contains("column")) {
      return {
        column: target,
        offsetY: offsetY,
      };
    }

    return this.getColumnAndOffsetY(
      target.parentElement,
      target.offsetTop + offsetY,
    );
  }

  getHoverTime(columnAndOffsetY) {
    const offsetYms = Math.floor(
      (columnAndOffsetY.offsetY / (this.rowHeight * 2)) *
        this.currentView.dayPartDuration.asMilliseconds(),
    );
    const hoverTime = this.currentView
      .fromDate()
      .add(offsetYms, "milliseconds");
    if (this.currentView.daysCount > 1) {
      hoverTime.add(columnAndOffsetY.column.cellIndex - 1, "days");
    }
    return hoverTime;
  }

  onDragOver(event: DragEvent) {
    if (
      event.dataTransfer.types.indexOf("text/media") === -1 &&
      event.dataTransfer.types.indexOf("text/show") === -1 &&
      event.dataTransfer.types.indexOf("text/feed") === -1 &&
      event.dataTransfer.types.indexOf("text/promo") === -1 &&
      event.dataTransfer.types.indexOf("text/layout") === -1 &&
      event.dataTransfer.types.indexOf("text/broadcast") === -1 &&
      event.dataTransfer.types.indexOf("text/block") === -1 &&
      event.dataTransfer.types.indexOf("text/durationadjust") === -1
    ) {
      return;
    }
    //console.log("calendar-grid->onDragOver");
    const columnAndOffsetY = this.getColumnAndOffsetY(
      event.target,
      event.offsetY,
    );

    if (!columnAndOffsetY) {
      this._dragMarkTop = null;
      this._dragMarkLeft = null;
      this._dragMarkWidth = null;
      this._dragSuggestedTime = null;
      this.changeDetectorRef.markForCheck();
      return;
    }

    const hoverTime = this.getHoverTime(columnAndOffsetY);
    const fromTime =
      this.currentView.daysCount > 1
        ? hoverTime.clone().startOf("day")
        : this.currentView.fromDate();

    const suggestedTime =
      event.dataTransfer.types.indexOf("text/durationadjust") === -1
        ? this.suggestTime(hoverTime)
        : this.suggestTimeForNewDuration(hoverTime);

    this._dragSuggestedTime = suggestedTime;
    this._dragMarkTop =
      ((suggestedTime.valueOf() - fromTime.valueOf()) /
        (this.currentView.dayPartDuration.asMilliseconds() / 2)) *
      this.rowHeight;
    this._dragMarkLeft = columnAndOffsetY.column.offsetLeft;
    this._dragMarkWidth = columnAndOffsetY.column.offsetWidth;
    this.changeDetectorRef.markForCheck();
    const showUnderMouse = this.findShowUnderMouse(hoverTime);
    if (showUnderMouse) {
      console.log(
        "onDragOverHandlerStream -> start ",
        suggestedTime.toString(),
        showUnderMouse.endTime.toString(),
      );
      // this.onDragOverHandlerStream.next(suggestedTime);
      this.onDragOverHandlerStream.next(showUnderMouse);
    }

    event.preventDefault();
  }

  onDragLeave(event: DragEvent, containerRect: ClientRect) {
    this._dragMarkTop = null;
    this._dragMarkLeft = null;
    this._dragMarkWidth = null;
    this._dragSuggestedTime = null;
    event.preventDefault();
  }

  async onDrop(event: DragEvent) {
    const suggestedTime = this._dragSuggestedTime;
    this._dragMarkTop = null;
    this._dragMarkLeft = null;
    this._dragMarkWidth = null;
    this._dragSuggestedTime = null;

    if (!suggestedTime) {
      return null;
    }

    const draggedMedia: MediaItem = event.dataTransfer.getData("text/media")
      ? JSON.parse(event.dataTransfer.getData("text/media"))
      : undefined;
    const draggedShow: Show = event.dataTransfer.getData("text/show")
      ? JSON.parse(event.dataTransfer.getData("text/show"))
      : undefined;
    const draggedFeed: LiveFeedInfo = event.dataTransfer.getData("text/feed")
      ? JSON.parse(event.dataTransfer.getData("text/feed"))
      : undefined;
    const durationAdjustShow: Show = event.dataTransfer.getData(
      "text/durationadjust",
    )
      ? JSON.parse(event.dataTransfer.getData("text/durationadjust"))
      : undefined;
    const draggedDynamicPromo: DynamicPromoInfo = event.dataTransfer.getData(
      "text/promo",
    )
      ? JSON.parse(event.dataTransfer.getData("text/promo"))
      : undefined;

    const draggedLayout: Layout = event.dataTransfer.getData("text/layout")
      ? JSON.parse(event.dataTransfer.getData("text/layout"))
      : undefined;
    const draggedBlock: Block = event.dataTransfer.getData("text/block")
      ? JSON.parse(event.dataTransfer.getData("text/block"))
      : undefined;

    let draggedBroadcastPreset: BroadcastPreset = event.dataTransfer.getData(
      "text/broadcast",
    )
      ? JSON.parse(event.dataTransfer.getData("text/broadcast"))
      : undefined;

    if (
      !draggedMedia &&
      !draggedShow &&
      !draggedFeed &&
      !this.durationAdjustShow &&
      !draggedDynamicPromo &&
      !draggedLayout &&
      !draggedBlock &&
      !draggedBroadcastPreset
    ) {
      return null;
    }
    if (draggedBlock && draggedBlock.isBlockStack == true) {
      this.ui.notify(this.ui.EVENTS.ShowAlert, {
        title: "Error",
        message: "Stacks can't be scheduled!",
      });
      return;
    }
    this.setIsLoading(true);
    let promise;
    if (!!draggedShow) {
      var media: MediaInfo = null;
      if (draggedShow && (draggedShow as any).mediaId) {
        media = await this.mediaRepository.getMediaInfo(
          (draggedShow as any).mediaId,
        );
      }
      promise = async () => {
        return await this.alertIfChangingCurrentlyPlayingItemMiddleware(
          suggestedTime,
          async () => {
            this.calendarColumns.forEach((column) =>
              column.forEach((calendarVideo: CalendarItem) => {
                if (calendarVideo.showId === draggedShow.id) {
                  calendarVideo.isDropped = true;
                }
              }),
            );
            return this.showRepository.updateShowTime.call(
              this.showRepository,
              draggedShow.id,
              suggestedTime,
            );
          },
          media,
        );
      };
    } else if (!!draggedMedia) {
      promise = async () => {
        const media = await this.mediaRepository.getMediaInfo(draggedMedia.id);
        if (media.segments && media.segments.length > 0) {
          return await this.alertIfChangingCurrentlyPlayingItemMiddleware(
            suggestedTime,
            async () => {
              return await this.mediaItemWithSegmentsDraggedEventHandler(
                suggestedTime,
                media,
              );
            },
            media,
          );
        } else {
          return await this.alertIfChangingCurrentlyPlayingItemMiddleware(
            suggestedTime,
            async () => {
              return await this.showRepository.createMediaShow(
                draggedMedia.id,
                suggestedTime,
              );
            },
            media,
          );
        }
      };
    } else if (!!draggedFeed) {
      promise = async () => {
        return await this.alertIfChangingCurrentlyPlayingItemMiddleware(
          suggestedTime,
          async () => {
            return await this.showRepository.createLiveShow.call(
              this.showRepository,
              draggedFeed.id,
              suggestedTime,
              this.feedDefaultDuration,
            );
          },
        );
      };
    } else if (!!draggedBroadcastPreset) {
      draggedBroadcastPreset = new BroadcastPreset(
        draggedBroadcastPreset.id,
        draggedBroadcastPreset.name,
        draggedBroadcastPreset.thumbnailUrl,
        draggedBroadcastPreset.switchers.map(
          (p) => new Switcher(p.name, p.items),
        ),
        [...draggedBroadcastPreset.audioChannels],
      );
      if (
        !draggedBroadcastPreset.hasPreviewSelected ||
        !draggedBroadcastPreset.hasProgramSelected
      ) {
        this.ui.notify(this.ui.EVENTS.ShowAlert, {
          title: "Error",
          message:
            "Both the program and preview windows of a broadcast must be filled before it can be scheduled!",
        });
        this._isLoading = false;
        return Promise.resolve();
      }

      promise = async () => {
        return await this.alertIfChangingCurrentlyPlayingItemMiddleware(
          suggestedTime,
          async () => {
            return await this.showRepository.createBroadcastPresetShow(
              draggedBroadcastPreset.id,
              suggestedTime,
              this.broadcastPresetShowDefaultDuration.asMilliseconds(),
            );
          },
        );
      };
    } else if (!!draggedLayout) {
      promise = async () => {
        return await this.alertIfChangingCurrentlyPlayingItemMiddleware(
          suggestedTime,
          async () => {
            let durationMs = this.feedDefaultDuration.asMilliseconds();
            const baseZone = draggedLayout.zones[0];
            if (!!baseZone.durationMs) {
              durationMs = baseZone.durationMs;
            } else if (baseZone.zoneType === ELayoutZoneType.media) {
              const baseZoneMedia = await this.mediaRepository.getMediaInfo(
                baseZone.mediaId,
              );
              durationMs = baseZoneMedia.contentDuration.asMilliseconds();
            }
            return await this.showRepository.createLayoutShow(
              draggedLayout.id,
              suggestedTime,
              durationMs,
            );
          },
        );
      };
    } else if (!!draggedDynamicPromo) {
      promise = async () => {
        return await this.alertIfChangingCurrentlyPlayingItemMiddleware(
          suggestedTime,
          async () => {
            return await this.showRepository.createDynamicPromoShow.call(
              this.showRepository,
              draggedDynamicPromo,
              suggestedTime,
            );
          },
        );
      };
    } else if (!!draggedBlock) {
      promise = async () => {
        const fromDate = moment().utc().subtract(100, "years");
        const toDate = moment().utc().add(100, "years");
        return await this.alertIfChangingCurrentlyPlayingItemMiddleware(
          suggestedTime,
          async () => {
            const foundShows = await this.showRepository.getByDates(
              draggedBlock.id,
              fromDate,
              toDate,
              true,
              null,
              true,
            );
            return await CopyShowsViewModel.showConfirmSecondaryEventSignalSettingsPopup(
              this.ui,
              this.showRepository,
              this.currentChannel,
              false,
              foundShows[0],
              foundShows.pop(),
              suggestedTime,
            );
          },
        );
      };
    } else {
      promise = async () => {
        return await this.alertIfChangingCurrentlyPlayingItemMiddleware(
          suggestedTime,
          async () => {
            return await this.showRepository.updateShowDuration.call(
              this.showRepository,
              durationAdjustShow.id,
              suggestedTime.diff(durationAdjustShow.startTime),
            );
          },
        );
      };
    }
    try {
      promise();
    } catch (err) {
      console.error(err);
      if (err.status === 404) {
        this.ui.notify(this.ui.EVENTS.ShowAlert, {
          title: "Reorder Show",
          message: "Media or channel not found",
        });
      } else if (err.status === 400) {
        this.ui.notify(this.ui.EVENTS.ShowAlert, {
          title: "Reorder Show",
          message: "Bad request",
        });
      } else if (err.status === 500) {
        this.ui.notify(this.ui.EVENTS.ShowAlert, {
          title: "Reorder Show",
          message:
            "Can't move the number of days defined for this channel. Please contact support.",
        });
      }
      this.setIsLoading(false);
      this.calendarColumns.forEach((column) =>
        column.forEach((video) => {
          (video as CalendarItem).isDropped = false;
        }),
      );
    } finally {
      this.setIsLoading(false);
    }
  }

  onMouseWhellScroll(event) {
    const currentDateBefore = this.currentView.currentDate.clone();
    const newState = this.currentView.onMouseWheelEventHandler(event);
    const currentDateAfter = this.currentView.currentDate.clone();

    if (currentDateBefore !== currentDateAfter) {
      this.currentDateChange.emit(newState);
    }
  }

  onScroll(event) {
    return;
    if (this._ignoreScroll) {
      this._ignoreScroll = false;
      return;
    }
    const dayPartDurationMs = this.currentView.dayPartDuration.asMilliseconds();
    const newCurrentDate =
      this.currentView.daysCount > 1
        ? this.currentView.currentDate.startOf("day")
        : this.currentView.fromDate();
    newCurrentDate.add(
      Math.floor(
        (this._scrollableContainer.nativeElement.scrollTop /
          (this.rowHeight * 2)) *
          dayPartDurationMs,
      ),
      "milliseconds",
    );
    if (this.currentView.daysCount > 1) {
      if (
        newCurrentDate.isBefore(this.currentView.currentDate.startOf("day"))
      ) {
        return;
      } else if (
        newCurrentDate.isSameOrAfter(
          this.currentView.currentDate.startOf("day").add(1, "day"),
        )
      ) {
        return;
      }
    } else {
      if (newCurrentDate.isBefore(this.currentView.fromDate())) {
        return;
      } else if (
        newCurrentDate.isSameOrAfter(
          this.currentView.fromDate().add(this.currentView.viewDuration),
        )
      ) {
        return;
      }
    }

    this.currentDateChange.emit({ currentDate: newCurrentDate });
  }

  calcTop(video: CalendarItem, from): number {
    const diffMs = video.startTime.valueOf() - from.valueOf();
    return Math.round(
      (diffMs / this.currentView.dayPartDuration.asMilliseconds()) *
        this.rowHeight *
        2,
    );
  }

  calcHeight(duration: moment.Duration): number {
    return (
      (duration.asMilliseconds() /
        this.currentView.dayPartDuration.asMilliseconds()) *
      this.rowHeight *
      2
    );
  }

  getActualHeight(height) {
    return Math.max(Math.round(height) - 3, rowMinHeight);
  }

  getVideosBetween(
    from: moment.Moment,
    to: moment.Moment,
    videos: CalendarItem[],
  ) {
    const addToEnd = moment.duration(this.currentView.dayPartDuration);
    return videos.filter((video) => {
      return (
        !video.endTime.isSameOrBefore(from) &&
        !video.startTime.isSameOrAfter(
          to.add((addToEnd.asHours() * 4) / 15, "hours"),
        )
      );
    });
  }

  toCalendarItems(shows: Show[]): CalendarItem[] {
    const calendarItems = [];

    let startIndex = 0;
    let height = 0;
    for (let i = 0; i < shows.length; i++) {
      const currentHeight = this.calcHeight(shows[i].duration);
      if (currentHeight <= maxHeightOfMultipleItem) {
        if (i === startIndex) {
          height = currentHeight;
          continue;
        } else {
          const duration = moment.duration(
            shows[i].endTime.diff(shows[i - 1].endTime),
          );
          const diffHeight = this.calcHeight(duration);
          if (diffHeight <= maxHeightOfMultipleItem) {
            height += diffHeight;
            continue;
          }
        }
      }

      if (startIndex < i) {
        // tslint:disable-next-line:no-shadowed-variable
        const calendarItem = new CalendarItem(shows.slice(startIndex, i));
        calendarItem.height = this.getActualHeight(height);
        calendarItems.push(calendarItem);
      }
      const calendarItem = new CalendarItem([shows[i]]);
      calendarItem.height = this.getActualHeight(currentHeight);
      calendarItems.push(calendarItem);

      startIndex = i + 1;
      height = 0;
    }

    if (startIndex < shows.length) {
      const calendarItem = new CalendarItem(
        shows.slice(startIndex, shows.length),
      );
      calendarItem.height = this.getActualHeight(height);
      calendarItems.push(calendarItem);
    }
    return calendarItems;
  }

  async getCalendarItemsInColumns() {
    const columns = new Array<CalendarItem[]>(this.currentView.daysCount);
    const fromTime = this.currentView.fromDate();
    const toTime =
      this.currentView.daysCount > 1
        ? fromTime.clone().add(this.currentView.daysCount, "days")
        : this.currentView.toDate();

    this.currentChannel = await this.userService.getCurrentChannel();
    const shows = await this.calendarService.getByDates(
      this.currentChannel.id,
      fromTime,
      toTime,
      false,
    );
    this.showsCache = new ShowsCache(shows);
    /*if (this.currentView.daysCount <= 1 && fromTime.isSame(moment(), 'day')) {
      await this.calendarService.update24HoursShowsCache(shows);
    }*/
    const calendarItems = this.toCalendarItems(shows);
    for (let i = 0; i < columns.length; i++) {
      const start = fromTime.clone().add(i, "days");
      const end = start.clone().add(i + 1, "days");
      columns[i] = calendarItems.filter(
        (x) => end.diff(x.startTime) > 0 && x.endTime.diff(start) > 0,
      );
      columns[i].forEach((item) => {
        item.topForColumn[i] = this.calcTop(
          item,
          this.currentView.fromDate().clone().add(i, "days"),
        );
      });
    }
    return columns;
  }

  updateCalendarItems() {
    let needToSetLoadingFalse;
    if (!this._isLoading) {
      this.setIsLoading(true);
      needToSetLoadingFalse = true;
    }
    return this.getCalendarItemsInColumns()
      .then((columns: CalendarItem[][]) => {
        this.calendarColumns = columns;
        console.log("getCalendarItemsInColumns", columns);
        this.changeDetectorRef.markForCheck();
        if (needToSetLoadingFalse) {
          this.setIsLoading(false);
        }
      })
      .catch((err) => {
        console.error(err);
        if (needToSetLoadingFalse) {
          this.setIsLoading(false);
        }
      });
  }

  setIsLoading(val: boolean) {
    this._isLoading = val;
    this.changeDetectorRef.markForCheck();
  }

  closeGap(calendarItem: CalendarItem) {
    this.alertIfChangingCurrentlyPlayingItemMiddleware(
      calendarItem.startTime,
      () => {
        this.setIsLoading(true);
        this.showRepository
          .closeGapAfter(calendarItem.items[calendarItem.items.length - 1])
          .then(() => {
            this.setIsLoading(false);
          })
          .catch((err) => {
            if (err) {
              this.ui.notify(this.ui.EVENTS.ShowAlert, {
                title: "Close Gap",
                message:
                  "Can't move the number of days defined for this channel. Please contact support.",
              });
            }
          });
      },
    );
  }

  removeVideo(calendarItem: CalendarItem) {
    this.setIsLoading(true);
    this.showRepository
      .deleteShow(calendarItem.showId)
      .then(() => {
        this.setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        this.setIsLoading(false);
      });
  }

  refresh() {
    this.lineOffsetTop = null;
    this.updateHeaders();
    this.updateDayParts();
    return this.updateCalendarItems();
  }

  needToRefresh(changes: SimpleChanges) {
    const previousView = changes["currentView"].previousValue;
    const currentView = changes["currentView"].currentValue;
    return (
      !previousView ||
      previousView.constructor !== currentView.constructor ||
      !previousView.fromDate().isSame(currentView.fromDate())
    );
  }

  viewChanged(currentView: SimpleChange) {
    return (
      !currentView.previousValue ||
      currentView.previousValue.constructor !==
        currentView.currentValue.constructor
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["currentView"]) {
      if (this.needToRefresh(changes)) {
        this.refresh().then(() => {
          setTimeout(() => this.scrollToCurrentTime());
        });
      } else {
        if (this.viewChanged(changes["currentView"])) {
          this.scrollToCurrentTime();
        }
      }
    }
    if (changes["refreshOnNow"] && changes["refreshOnNow"].currentValue) {
      this.scrollToCurrentTime();
    }
  }

  scrollToCurrentTime() {
    const diffInMs =
      this.currentView.daysCount > 1
        ? this.currentView.currentDate.valueOf() -
          this.currentView.currentDate.startOf("day").valueOf()
        : this.currentView.currentDate.valueOf() -
          this.currentView.fromDate().valueOf();

    const newScrollTop = Math.floor(
      (diffInMs / this.currentView.dayPartDuration.asMilliseconds()) *
        this.rowHeight *
        2,
    );
    if (this._scrollableContainer.nativeElement.scrollTop !== newScrollTop) {
      this._ignoreScroll = true;
    }
    this._scrollableContainer.nativeElement.scrollTop = newScrollTop;
    this.refreshOnNow = false;
    this.refreshOnNowChange.emit(this.refreshOnNow);
  }

  async onVideoClick(video: CalendarItem) {
    if (video.items.length > 1) {
      if (!this.canZoomIn()) {
        return;
      }
      this.zoomIn.emit(video.items[0].startTime);
    } else {
      const show = video.items[0];
      if (show instanceof MediaShow) {
        return this.router.navigate(["/mam/editMedia", show.mediaId], {
          queryParamsHandling: "preserve",
        });
      } else if (show instanceof LiveShow) {
        return this.router.navigate(["/scheduler/live/edit/", show.feedId], {
          queryParamsHandling: "preserve",
        });
      } else if (show instanceof LayoutShow) {
        return this.router.navigate(["/layouts/edit/", show.layoutUid], {
          queryParamsHandling: "preserve",
        });
      } else if (show instanceof BroadcastPresetShow) {
        const globalBroadcastMode =
          await this._channelBroadcastModeChecker.getGlobalBroadcastMode();
        if (globalBroadcastMode !== EChannelBroadcastMode.Off) {
          return this.router.navigate(
            ["/broadcasts/live/", show.broadcastPresetUid],
            {
              queryParamsHandling: "preserve",
              state: {
                fromScheduler: true,
              },
            },
          );
        } else {
          return this.router.navigate(
            ["/broadcasts/edit/", show.broadcastPresetUid],
            {
              queryParamsHandling: "preserve",
              state: {
                fromScheduler: true,
              },
            },
          );
        }
      }
    }
  }

  isEndOfView(index) {
    return this._dayParts.length - 1 === index;
  }

  onDaypartClick(event: MouseEvent, daypart: { id: number; text: string }) {
    if (!this.canZoomIn()) {
      return;
    }
    const days = Math.floor(
      (this.currentView.currentDate.valueOf() -
        this.currentView.fromDate().valueOf()) /
        (24 * 60 * 60 * 1000),
    );
    const timeMs =
      (event.offsetY / (this.rowHeight * 2)) *
        this.currentView.dayPartDuration.asMilliseconds() +
      daypart.id +
      days * 24 * 60 * 60 * 1000;

    this.zoomIn.emit(moment(timeMs));
  }

  onDaypartRightClick(event: MouseEvent, daypart: { id: number; text: string }) {
    if (!this.canZoomOut()) {
      return;
    }

    const days = Math.floor(
      (this.currentView.currentDate.valueOf() -
        this.currentView.fromDate().valueOf()) /
        (24 * 60 * 60 * 1000),
    );
    const timeMs =
      (event.offsetY / (this.rowHeight * 2)) *
        this.currentView.dayPartDuration.asMilliseconds() +
      daypart.id +
      days * 24 * 60 * 60 * 1000;

    this.zoomOut.emit(moment(timeMs));
    event.preventDefault();
  }

  canZoomIn() {
    return !(this.currentView instanceof FiveMinCalendarView);
  }

  canZoomOut() {
    return !(this.currentView instanceof WeekCalendarView);
  }

  showLineWeekView(columnIndex) {
    return moment().day() === columnIndex;
  }

  showLineAndTriangle() {
    if (this.calendarService.DuringSkip) {
      return false;
    } // hacky! remove of skip ui hack is no longer needed.

    const now = moment();
    return (
      now.isSameOrAfter(this.currentView.fromDate()) &&
      this.currentView
        .fromDate()
        .add(this.currentView.viewDuration)
        .isAfter(now)
    );
  }

  isWeekView() {
    return this.currentView instanceof WeekCalendarView;
  }

  isDayView() {
    return this.currentView instanceof DayCalendarView;
  }

  currentIndicatorOffsetTop() {
    if (this.lineOffsetTop) {
      return this.lineOffsetTop;
    }
    const obj = { startTime: moment() };
    this.lineOffsetTop = !this.isWeekView()
      ? this.calcTop(obj as any, this.currentView.fromDate().clone())
      : this.calcTop(obj as any, obj.startTime.clone().startOf("day"));
    this.lineOffsetTop = this.lineOffsetTop - 3.5;
    return this.lineOffsetTop;
  }

  ngOnDestroy() {
    clearInterval(this.interval);
    if (this.mediaUpdatedSubscription) {
      this.mediaUpdatedSubscription.unsubscribe();
    }

    if (this.mediaPackshotUploadFinishedSubscription) {
      this.mediaPackshotUploadFinishedSubscription.unsubscribe();
    }

    if (this.showsChangedSubscription) {
      this.showsChangedSubscription.unsubscribe();
    }

    if (this.changeCurrentDateUpdated) {
      this.changeCurrentDateUpdated.unsubscribe();
    }

    if (this.searchResultHighlightedUpdated) {
      this.searchResultHighlightedUpdated.unsubscribe();
    }

    if (this.onMouseWheelEventStreamUnsibscribe$) {
      this.onMouseWheelEventStreamUnsibscribe$.next();
      this.onMouseWheelEventStreamUnsibscribe$.complete();
    }

    if (this.onDragOverHandlerStream) {
      this.onDragOverHandlerStream.unsubscribe();
    }
    this.socketService.stopListeningForSchedulerUpdates(
      this.scheduleUpdateListenerHandler,
    );
  }

  trackById(index, item: any) {
    return item.id;
  }

  openReplaceShowModal(video: CalendarItem) {
    this.ngxSmartModalService.setModalData(
      {
        show: video.show,
        channelFramerate: this.channelFramerate,
        frameFormat: this.currentChannel.framerate,
      },
      "replaceShowModal",
      true,
    );
    const call = () => {
      this.ngxSmartModalService.getModal("replaceShowModal").open();
    };
    if (video.startTime.isSameOrBefore(moment())) {
      this.ui.globalNotify(this.ui.EVENTS.ChangePlayingItem, call, null);
      return;
    } else {
      call();
    }
    this.changeDetectorRef.markForCheck();
  }

  openRenameModal(video: CalendarItem) {
    this.ngxSmartModalService.setModalData(
      {
        showId: video.showId,
        oldName: video.itemName,
      },
      "renameShowModal",
      true,
    );
    this.ngxSmartModalService.getModal("renameShowModal").open();
    this.changeDetectorRef.markForCheck();
  }

  async openChannelFuturePreview(item: CalendarItem) {
    if (this.futurePreviewViewModel) {
      this.futurePreviewViewModel.closeWindow();
      this.futurePreviewViewModel.showWindow();
      await this.futurePreviewViewModel.setShow(
        item.show.clone(),
        this.channelTimezone,
      );
    }
  }

  chooseSegmentClickedEventHandler(item: CalendarItem) {
    this.ngxSmartModalService.setModalData(
      {
        mediaId: item.mediaId,
        calendarItem: item,
        onSegmentsSelectedCallback: (segments) => {
          this.updateCurrentShowWithSegment(segments, item).then();
        },
        currentChannel: this.currentChannel,
      },
      "chooseSegmentModal",
      true,
    );
    const call = () => {
      this.ngxSmartModalService.getModal("chooseSegmentModal").open();
    };
    if (item.startTime.isSameOrBefore(moment())) {
      this.ui.globalNotify(this.ui.EVENTS.ChangePlayingItem, call, null);
      return;
    } else {
      call();
    }
    this.changeDetectorRef.markForCheck();
  }
  private mediaItemWithSegmentsDraggedEventHandler(
    startTime: moment.Moment,
    media: MediaInfo,
  ) {
    this.ngxSmartModalService.setModalData(
      {
        mediaId: media.id,
        onSegmentsSelectedCallback: (segments) => {
          this._createMediaShowsFromSegments(startTime, segments, media).then();
        },
        currentChannel: this.currentChannel,
        allowMultiSelect: true,
      },
      "chooseSegmentModal",
      true,
    );
    this.ngxSmartModalService.getModal("chooseSegmentModal").open();
    this.changeDetectorRef.markForCheck();
  }
  private async _createMediaShowsFromSegments(
    startTime: moment.Moment,
    segments: MediaSegment[],
    media: MediaInfo,
  ) {
    let currentStartTime = startTime;
    if (segments && segments.length > 0) {
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const durationMs = segment.timeOutMs - segment.timeInMs;
        let newShowName = "";
        if (segment.tenantUniqueId) {
          newShowName = `${media.name}, Segment ID: ${segment.tenantUniqueId}`;
        } else {
          newShowName = media.name;
        }
        await this.showRepository.createMediaShow(
          media.id,
          currentStartTime,
          segment.timeInMs,
          durationMs,
          newShowName,
        );
        currentStartTime = currentStartTime.add(durationMs, "milliseconds");
      }
    }
  }

  getUtcOffset() {
    const offsetInHours = this.currentView.fromDate().utcOffset() / 60;
    let formatted = offsetInHours.toString();
    if (formatted.length < 2) {
      formatted = "0" + formatted;
    }
    if (offsetInHours >= 0) {
      formatted = "+" + formatted;
    }
    return "GMT" + formatted;
  }
  public async updateCurrentShowWithSegment(
    segments: MediaSegment[],
    calendarItem: CalendarItem,
  ) {
    if (segments && segments.length > 0) {
      const segment = segments[0];
      const show = calendarItem.show as MediaShow;
      const durationMs = segment.timeOutMs - segment.timeInMs;

      await this.showRepository.updateShowSom(show.id, segment.timeInMs, false);
      await this.showRepository.updateShowDuration(show.id, durationMs, false);
      let newShowName = "";
      const media = await this.mediaRepository.getMediaInfo(show.mediaId);
      if (segment.tenantUniqueId) {
        newShowName = `${media.name}, Segment ID: ${segment.tenantUniqueId}`;
      } else {
        newShowName = media.name;
      }
      await this.showRepository.renameShow(show.id, newShowName, true);
    }
  }
  onsearchResultHighlightedUpdated(showId: string) {
    if (this.isWeekView()) {
      return;
    }
    const show = this.calendarService.getShowById(showId);
    if (show) {
      this.currentDateChange.emit({
        currentDate: moment.tz(show.startTime, this.channelTimezone),
      });
    }
  }

  public isChooseSegmentMenuVisible(item: CalendarItem): boolean {
    return item.isMediaShow && item?.show?.isAnchored == false;
  }
  changeAnchorState(item: CalendarItem) {
    this.showRepository.updateShowAnchorState(
      item.show.id,
      !item.show.isAnchored,
    );
  }
  showAnchorMenuItemText(item: CalendarItem) {
    return `${item.show.isAnchored ? "Una" : "A"}nchor Show`;
  }

  isReplaceShowMenuVisible(item: CalendarItem) {
    return item?.show?.isAnchored == false;
  }
}
